import { useCallback, useEffect, useState, useMemo, useRef } from "react";
import { Button, Container } from "reactstrap";

import ReactTable from "components/ReactTable/ReactTable";
import useFetch from "Hooks/useFetch";
import styles from "Pages/Admin/AdminConfiguration/AdminConfiguration.module.css";
import { format } from "date-fns";
import usePageTitle from "Hooks/usePageTitle";

export default function StuckJobs() {
    const [jobs, setJobs] = useState<IJob[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedRows, setSelectedRows] = useState<any[]>([]);
    const [allRowsSelected, setAllRowsSelected] = useState<any>(undefined);

    usePageTitle("Unfinished Jobs");
    const { get, post } = useFetch();
    const ignore = useRef(false);

    const getStuckJobs = useCallback(async () => {
        setIsLoading(true);

        const response = await get(`api/admin/stuckjobs`);

        let data: Response | IJob[];
        try {
            data = await response.json();
            console.log(data);
        } catch (e) {
            data = response;
        }
        if (response.ok && !ignore.current) {
            data = data as IJob[];
            setJobs(data);
        }
        setIsLoading(false);
    }, [get]);

    useEffect(() => {
        ignore.current = false;

        getStuckJobs();
        return () => {
            ignore.current = true;
        };
    }, [get, getStuckJobs, post]);

    const onSetSelectedRows = useCallback((row: any) => {
        setSelectedRows((prev) => {
            const ids = prev.map((r) => r?.id);
            if (ids?.includes(row?.original.id)) {
                return prev.filter((r) => r?.id !== row?.original.id);
            } else {
                return [...prev, row.original];
            }
        });
    }, []);

    const onToggleAllRowsSelected = useCallback((rowObjects: any) => {
        const rows = rowObjects.map((row: any) => row.original);
        setSelectedRows(rows);
    }, []);

    async function abandonJobs() {
        const url = `/api/admin/stuckjobs/abandon`;
        const ids = selectedRows.map((r) => parseInt(r.id));
        const body = ids;

        const response = await post(url, body);
        let data;
        try {
            data = await response.json();
        } catch (e) {
            data = response;
        }
        console.log(data);

        getStuckJobs();
    }

    async function toggleAllRowsSelected() {
        setAllRowsSelected((prev: boolean) => !prev);
    }

    const columns = useMemo(() => {
        const cellContent = (value: string) => {
            return <span className={styles.textBox}>{value}</span>;
        };
        return [
            {
                Header: "Created Time",
                accessor: "created_time",
                style: { flexWrap: "nowrap" },
                Cell: ({ value }: { value: string }) =>
                    cellContent(
                        value
                            ? format(new Date(value), "MMM d, HH:mm:ss")
                            : "null"
                    ),
            },
            {
                Header: "Company ID",
                accessor: "company_id",
                Cell: ({ value }: { value: string }) => cellContent(value),
            },
            {
                Header: "Initiator",
                accessor: "initiator",
                Cell: ({ value }: { value: string }) => cellContent(value),
            },
            {
                Header: "Machine",
                accessor: "machine_name",
                Cell: ({ value }: { value: string }) => cellContent(value),
            },
            {
                Header: "Method Info",
                accessor: "method_info",
                Cell: ({ value }: { value: string }) => cellContent(value),
            },
            {
                Header: "Status",
                accessor: "status",
                Cell: ({ value }: { value: string }) => cellContent(value),
            },

            {
                Header: "Start Time",
                accessor: "start_time",
                Cell: ({ value }: { value: string }) =>
                    cellContent(
                        value
                            ? format(new Date(value), "MMM d, HH:mm:ss")
                            : "null"
                    ),
            },

            {
                Header: "Finish Time",
                accessor: "finish_time",
                Cell: ({ value }: { value: string }) =>
                    cellContent(
                        value
                            ? format(new Date(value), "MMM d, HH:mm:ss")
                            : "null"
                    ),
            },
        ];
    }, []);

    const tableProps = {
        columns,
        data: jobs,
        showPagination: true,
        onSetSelectedRows,
        allRowsSelected,
        onToggleAllRowsSelected,
        selectedRows,
        isLoading,
    };

    const selectLabel = allRowsSelected ? "Clear Selection" : "Select All";
    const selectColor = allRowsSelected ? "secondary" : "primary";

    return (
        <Container fluid>
            {/* Header and Select/Abandon buttons */}
            <div className={styles.headerAndButtonsBox}>
                <h1>Unfinished Jobs</h1>
                <span className={styles.buttonsBox}>
                    <Button color="danger" onClick={(e) => abandonJobs()}>
                        Abandon
                    </Button>
                    <Button color={selectColor} onClick={toggleAllRowsSelected}>
                        {selectLabel}
                    </Button>
                </span>
            </div>

            <ReactTable {...tableProps} />
        </Container>
    );
}
